import { ChevronDown, ChevronUp, Search, Star } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import { BROWSE_STAGES, FACET_SHORT_LIST, type CandidateBrowseFacets, type FacetOption } from '@nest-recruit/shared';
import { Checkbox } from '../../../../components/Checkbox';
import { DatePicker } from '../../../../components/DatePicker';
import { Field } from '../../../../components/Field';
import { Input, SearchInput } from '../../../../components/Input';
import { ModuleGate } from '../../../../components/ModuleGate';
import { MultiSelect, Select } from '../../../../components/Select';
import { Segmented } from '../../../../components/Segmented';
import { Skeleton } from '../../../../components/Skeleton';
import { toneClass } from '../../../../components/Tone';
import { formatNumber } from '../../../../lib/format';
import { useCustomFields } from '../../../admin/api/settings-api';
import { usePoolOptions } from '../../../pools/api/pools-api';
import { listOf, toggleInList, withFilter, withList, type CandidateBrowseSearch, type FilterKey, type ListKey } from './browse-address';
import { BROWSE_STAGE_TONES } from './list-format';
import styles from './FilterPanel.module.css';

/** Typing settles for this long before the list is asked again. */
const TYPING_SETTLE_MS = 250;
/** A folded section names its three commonest options under its title. */
const NOTE_OPTIONS = 3;

interface FilterPanelProps {
  readonly search: CandidateBrowseSearch;
  readonly facets: CandidateBrowseFacets | undefined;
  readonly onChange: (next: CandidateBrowseSearch) => void;
  readonly onClearAll: () => void;
  readonly skillSearch: string;
  readonly onSkillSearch: (value: string) => void;
  /** §L23: Expected salary only for a viewer who may see salary. */
  readonly canSeeSalary: boolean;
  /** The panel beside the list: its own heading, Clear all, and the page's one search box for `/` (06-qol §2). The sheet has its own. */
  readonly isPrimary: boolean;
}

/**
 * CAND-01's filters with POOL-04's folded in (owner, 2026-10-18 "Merge into one page"). Every
 * option shows how many people it would leave (Q3 "All options"); every change goes to the address.
 */
export function FilterPanel({ search, facets, onChange, onClearAll, skillSearch, onSkillSearch, canSeeSalary, isPrimary }: FilterPanelProps): ReactNode {
  const set = (key: FilterKey, value: string | undefined): void => onChange(withFilter(search, key, value));
  const toggle = (key: ListKey, value: string): void => onChange(toggleInList(search, key, value));
  const isLoading = facets === undefined;

  return (
    <div className={styles.panel}>
      {isPrimary && (
        <div className={styles.top}>
          <h2 className={styles.heading}>Filters</h2>
          <button type="button" className={styles.link} onClick={onClearAll}>
            Clear all
          </button>
        </div>
      )}
      <TextSearch value={search.text} onCommit={(value) => set('text', value)} isPrimary={isPrimary} />
      <p className={styles.hint}>Spellings like “Md.” and “Mohammad” find each other. Words are looked for in titles and application answers.</p>

      <Section title="Stage" isOpenAtFirst>
        <Options
          isLoading={isLoading}
          options={facets?.stages ?? []}
          selected={listOf(search, 'stages')}
          onToggle={(value) => toggle('stages', value)}
          leadOf={(value) => {
            const stage = BROWSE_STAGES.find((item) => item === value);
            return stage === undefined ? null : <span className={`${toneClass} ${styles.dot}`} data-tone={BROWSE_STAGE_TONES[stage]} aria-hidden />;
          }}
        />
      </Section>

      <Section title="Job applied for" isOpenAtFirst>
        <Options isLoading={isLoading} options={facets?.jobs ?? []} selected={listOf(search, 'jobs')} onToggle={(value) => toggle('jobs', value)} shortList={FACET_SHORT_LIST} moreNoun="jobs" />
      </Section>

      <Section title="Tags" isOpenAtFirst>
        {!isLoading && (
          <Option
            label="Starred by me"
            count={facets.starredByMe}
            isOn={search.starred === 'me'}
            onToggle={() => set('starred', search.starred === 'me' ? undefined : 'me')}
            lead={<Star size={14} className={styles.star} aria-hidden />}
          />
        )}
        <Options isLoading={isLoading} options={facets?.tags ?? []} selected={listOf(search, 'tags')} onToggle={(value) => toggle('tags', value)} shortList={FACET_SHORT_LIST} moreNoun="tags" />
      </Section>

      <Section title="Skills" isOpenAtFirst>
        <Segmented
          label="How skills combine"
          value={search.skillMode === 'any' ? 'any' : 'all'}
          options={[
            { value: 'all', label: 'All skills' },
            { value: 'any', label: 'Any skill' },
          ]}
          onChange={(value) => set('skillMode', value === 'any' ? 'any' : undefined)}
        />
        <SkillFind value={skillSearch} onSettle={onSkillSearch} />
        <Options isLoading={isLoading} options={facets?.skills ?? []} selected={listOf(search, 'skills')} onToggle={(value) => toggle('skills', value)} shortList={FACET_SHORT_LIST} moreNoun="skills" />
      </Section>

      <Section title="Experience" isOpenAtFirst>
        <Options isLoading={isLoading} options={facets?.experience ?? []} selected={listOf(search, 'experience')} onToggle={(value) => toggle('experience', value)} />
      </Section>

      <Section title="Education, at least" note={noteOf(facets?.education)} isOpenAtFirst={search.education !== undefined}>
        <Options
          isLoading={isLoading}
          options={facets?.education ?? []}
          selected={search.education === undefined ? [] : [search.education]}
          onToggle={(value) => set('education', search.education === value ? undefined : value)}
        />
      </Section>

      <Section title="City" note={noteOf(facets?.cities)} isOpenAtFirst={search.cities !== undefined}>
        <Options isLoading={isLoading} options={facets?.cities ?? []} selected={listOf(search, 'cities')} onToggle={(value) => toggle('cities', value)} shortList={FACET_SHORT_LIST} moreNoun="cities" />
      </Section>

      {canSeeSalary && (
        <Section
          title="Expected salary (৳)"
          note="From – to, a month"
          isOpenAtFirst={search.salaryMin !== undefined || search.salaryMax !== undefined || search.overBudget !== undefined}
        >
          <div className={styles.pair}>
            <CommitInput label="From" prefix="৳" value={search.salaryMin} onCommit={(value) => set('salaryMin', value)} />
            <CommitInput label="To" prefix="৳" value={search.salaryMax} onCommit={(value) => set('salaryMax', value)} />
          </div>
          {/* Q6 (DECISIONS-LOG 2026-10-19): asks above the job's budget top. The server sends no count to
              a viewer who may not see salaries (#5), and then the box is not there at all. */}
          {facets !== undefined && facets.overBudget !== null && (
            <Option
              label="Over budget"
              count={facets.overBudget}
              isOn={search.overBudget === 'yes'}
              onToggle={() => set('overBudget', search.overBudget === 'yes' ? undefined : 'yes')}
            />
          )}
        </Section>
      )}

      <Section title="Notice period" note="At most N days" isOpenAtFirst={search.maxNoticeDays !== undefined}>
        <CommitInput label="At most" suffix="days" value={search.maxNoticeDays} onCommit={(value) => set('maxNoticeDays', value)} />
      </Section>

      <Section title="Last stage reached" note="Where their latest application got to" isOpenAtFirst={search.reached !== undefined}>
        <Options isLoading={isLoading} options={facets?.reached ?? []} selected={listOf(search, 'reached')} onToggle={(value) => toggle('reached', value)} />
      </Section>

      <Section title="Source" note={noteOf(facets?.sources)} isOpenAtFirst={search.sources !== undefined}>
        <Options isLoading={isLoading} options={facets?.sources ?? []} selected={listOf(search, 'sources')} onToggle={(value) => toggle('sources', value)} shortList={FACET_SHORT_LIST} moreNoun="sources" />
      </Section>

      <Section title="Applied between" note="From – to" isOpenAtFirst={search.from !== undefined || search.to !== undefined}>
        <DateRange from={search.from} to={search.to} onFrom={(value) => set('from', value)} onTo={(value) => set('to', value)} label="Applied" />
      </Section>

      <Section title="Added on" isOpenAtFirst={search.addedFrom !== undefined || search.addedTo !== undefined}>
        <DateRange from={search.addedFrom} to={search.addedTo} onFrom={(value) => set('addedFrom', value)} onTo={(value) => set('addedTo', value)} label="Added" />
      </Section>

      <ModuleGate module="pools">
        <PoolsSection search={search} onChange={onChange} />
      </ModuleGate>

      <ModuleGate module="custom_fields">
        <CustomFieldSection search={search} onChange={onChange} />
      </ModuleGate>
    </div>
  );
}

/** "Bachelor's 803 · Master's 312 · Diploma 64": what a folded section holds, at a glance. */
function noteOf(options: readonly FacetOption[] | undefined): string | undefined {
  if (!options || options.length === 0) return undefined;
  return [...options]
    .sort((a, b) => b.count - a.count)
    .slice(0, NOTE_OPTIONS)
    .map((option) => `${option.label} ${formatNumber(option.count)}`)
    .join(' · ');
}

function Section({ title, note, isOpenAtFirst = false, children }: { readonly title: string; readonly note?: string; readonly isOpenAtFirst?: boolean; readonly children: ReactNode }): ReactNode {
  const [isOpen, setIsOpen] = useState(isOpenAtFirst);
  const bodyId = useId();
  const Chevron = isOpen ? ChevronUp : ChevronDown;
  return (
    <div className={styles.section}>
      <button type="button" className={styles.sectionHead} aria-expanded={isOpen} aria-controls={bodyId} onClick={() => setIsOpen((open) => !open)}>
        <span className={styles.sectionTitle}>
          {title}
          {!isOpen && note !== undefined && <span className={styles.note}>{note}</span>}
        </span>
        <Chevron size={14} aria-hidden />
      </button>
      {isOpen && (
        <div id={bodyId} className={styles.sectionBody}>
          {children}
        </div>
      )}
    </div>
  );
}

interface OptionsProps {
  readonly isLoading: boolean;
  readonly options: readonly FacetOption[];
  readonly selected: readonly string[];
  readonly onToggle: (value: string) => void;
  readonly leadOf?: (value: string) => ReactNode;
  /** Long lists show this many, then "+ Show N more {noun}". */
  readonly shortList?: number;
  readonly moreNoun?: string;
}

function Options({ isLoading, options, selected, onToggle, leadOf, shortList, moreNoun = 'options' }: OptionsProps): ReactNode {
  const [isShowingAll, setIsShowingAll] = useState(false);
  if (isLoading) {
    return (
      <div className={styles.loading} aria-hidden>
        <Skeleton width="80%" />
        <Skeleton width="60%" />
        <Skeleton width="70%" />
      </div>
    );
  }
  if (options.length === 0) return <p className={styles.none}>None yet</p>;
  const limit = shortList !== undefined && !isShowingAll ? shortList : options.length;
  // A ticked option is never hidden behind "Show more".
  const shown = options.filter((option, index) => index < limit || selected.includes(option.value));
  const hidden = options.length - shown.length;
  return (
    <>
      {shown.map((option) => (
        <Option key={option.value} label={option.label} count={option.count} isOn={selected.includes(option.value)} onToggle={() => onToggle(option.value)} lead={leadOf?.(option.value)} />
      ))}
      {hidden > 0 && (
        <button type="button" className={styles.link} onClick={() => setIsShowingAll(true)}>
          + Show {formatNumber(hidden)} more {moreNoun}
        </button>
      )}
      {isShowingAll && shortList !== undefined && options.length > shortList && (
        <button type="button" className={styles.link} onClick={() => setIsShowingAll(false)}>
          Show fewer
        </button>
      )}
    </>
  );
}

function Option({ label, count, isOn, onToggle, lead }: { readonly label: string; readonly count: number; readonly isOn: boolean; readonly onToggle: () => void; readonly lead?: ReactNode }): ReactNode {
  return (
    // The whole row toggles, as a label would; the box itself is the keyboard's way in.
    <div className={styles.option} data-on={isOn || undefined} onClick={onToggle} role="presentation">
      <span className={styles.box} onClick={(event) => event.stopPropagation()} role="presentation">
        <Checkbox checked={isOn} onChange={onToggle} ariaLabel={`${label}, ${formatNumber(count)}`} />
      </span>
      {lead}
      <span className={styles.optionLabel}>{label}</span>
      <span className={styles.count}>{formatNumber(count)}</span>
    </div>
  );
}

/** The search box: the list follows as the typing settles. A change from outside (Clear all, a view) replaces the draft. */
function TextSearch({ value, onCommit, isPrimary }: { readonly value: string | undefined; readonly onCommit: (value: string | undefined) => void; readonly isPrimary: boolean }): ReactNode {
  const [draft, setDraft] = useState(value ?? '');
  const [seen, setSeen] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  if (seen !== value) {
    setSeen(value);
    if ((value ?? '') !== draft.trim()) setDraft(value ?? '');
  }
  const change = (next: string): void => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(next.trim() === '' ? undefined : next.trim()), TYPING_SETTLE_MS);
  };
  return (
    <SearchInput
      aria-label="Search by name, email, mobile or a word in their answers"
      placeholder="Name, email, mobile or a word"
      value={draft}
      onChange={(event) => change(event.target.value)}
      onClear={() => change('')}
      data-primary-search={isPrimary || undefined}
    />
  );
}

/** "Find a skill" narrows the skill list as the typing settles, not on every key. */
function SkillFind({ value, onSettle }: { readonly value: string; readonly onSettle: (value: string) => void }): ReactNode {
  const [draft, setDraft] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const change = (next: string): void => {
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSettle(next), TYPING_SETTLE_MS);
  };
  return <Input icon={Search} aria-label="Find a skill" placeholder="Find a skill" value={draft} onChange={(event) => change(event.target.value)} onClear={() => change('')} />;
}

/** A typed number commits on Enter or on leaving the box, so each keystroke is not a search. */
function CommitInput({ label, value, prefix, suffix, onCommit }: { readonly label: string; readonly value: string | undefined; readonly prefix?: string; readonly suffix?: string; readonly onCommit: (value: string | undefined) => void }): ReactNode {
  const [draft, setDraft] = useState(value ?? '');
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(value ?? '');
  }
  const commit = (): void => {
    const digits = draft.replace(/\D/g, '');
    const next = digits === '' ? undefined : digits;
    if (next !== value) onCommit(next);
  };
  return (
    <Field label={label}>
      {({ inputId }) => (
        <Input
          id={inputId}
          value={draft}
          prefix={prefix}
          suffix={suffix}
          inputMode="numeric"
          isNumeric
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
          }}
        />
      )}
    </Field>
  );
}

function DateRange({ from, to, onFrom, onTo, label }: { readonly from: string | undefined; readonly to: string | undefined; readonly onFrom: (value: string | undefined) => void; readonly onTo: (value: string | undefined) => void; readonly label: string }): ReactNode {
  return (
    <div className={styles.pair}>
      <Field label="From">
        {({ inputId }) => <DatePicker id={inputId} label={`${label} from`} value={from ?? ''} onChange={(value) => onFrom(value === '' ? undefined : value)} />}
      </Field>
      <Field label="To">
        {({ inputId }) => <DatePicker id={inputId} label={`${label} to`} value={to ?? ''} onChange={(value) => onTo(value === '' ? undefined : value)} />}
      </Field>
    </div>
  );
}

/** Talent pools, only while that module is on (#13). */
function PoolsSection({ search, onChange }: { readonly search: CandidateBrowseSearch; readonly onChange: (next: CandidateBrowseSearch) => void }): ReactNode {
  const pools = usePoolOptions();
  return (
    <Section title="Talent pools" isOpenAtFirst={search.pools !== undefined || search.pool !== undefined}>
      <Field label="In these pools">
        {({ inputId }) => (
          <MultiSelect
            id={inputId}
            label="In these pools"
            value={listOf(search, 'pools')}
            options={(pools.data ?? []).map((pool) => ({ value: pool.id, label: pool.name }))}
            onChange={(values) => onChange(withList(search, 'pools', values))}
          />
        )}
      </Field>
      <Segmented
        label="Pool membership"
        value={search.pool ?? 'either'}
        options={[
          { value: 'either', label: 'Either' },
          { value: 'any_pool', label: 'In a pool' },
          { value: 'no_pool', label: 'In none' },
        ]}
        onChange={(value) => onChange(withFilter(search, 'pool', value === 'either' ? undefined : value))}
      />
    </Section>
  );
}

/** A candidate custom field and the text its value holds, only while Custom fields is on. */
function CustomFieldSection({ search, onChange }: { readonly search: CandidateBrowseSearch; readonly onChange: (next: CandidateBrowseSearch) => void }): ReactNode {
  const fields = useCustomFields('candidate');
  const options = fields.data ?? [];
  const [draft, setDraft] = useState(search.customValue ?? '');
  if (options.length === 0) return null;
  const commitValue = (): void => {
    const next = draft.trim() === '' ? undefined : draft.trim();
    if (next !== search.customValue) onChange(withFilter(search, 'customValue', next));
  };
  return (
    <Section title="Custom field" isOpenAtFirst={search.customField !== undefined}>
      <Field label="Field">
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Custom field"
            value={search.customField ?? ''}
            options={[{ value: '', label: 'Any' }, ...options.map((field) => ({ value: field.id, label: field.label }))]}
            onChange={(value) => onChange(withFilter(withFilter(search, 'customField', value === '' ? undefined : value), 'customValue', value === '' ? undefined : search.customValue))}
          />
        )}
      </Field>
      {search.customField !== undefined && (
        <Field label="Contains">
          {({ inputId }) => (
            <Input
              id={inputId}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commitValue}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitValue();
              }}
            />
          )}
        </Field>
      )}
    </Section>
  );
}
